import type { ShopperSearch, ShopperExperience } from '@/scapi';

/**
 * Query keys accepted by the Shopper Search `productSearch` SCAPI operation (OpenAPI-generated).
 * Does not include storefront-only URL params such as `page` (pagination uses `limit`/`offset` in SCAPI).
 */
type ScapiProductSearchQueryKeys = keyof ShopperSearch.operations['productSearch']['parameters']['query'];

/**
 * Query keys accepted by the Shopper Experience `searchContent` SCAPI operation (OpenAPI-generated).
 * Does not include storefront-only URL params such as `page` (pagination uses `limit`/`offset` in SCAPI).
 */
type ScapiContentSearchQueryKeys = keyof ShopperExperience.operations['searchContent']['parameters']['query'];

/**
 * Keys we read/write in product/category search URLs: SCAPI keys plus storefront conventions.
 * `page` is not an SCAPI parameter; it is translated to `offset`/`limit` before calling the API.
 */
export type ProductSearchQueryKeys = ScapiProductSearchQueryKeys | 'page';

/**
 * Keys we read/write in content search URLs: SCAPI keys plus storefront conventions.
 * `page` is not an SCAPI parameter; it is translated to `offset`/`limit` before calling the API.
 */
export type ContentSearchQueryKeys = ScapiContentSearchQueryKeys | 'page';

/**
 * Type-safe query parameter constants for product search
 * These constants ensure we use the correct parameter names as defined in the SCAPI client types
 */
export const PRODUCT_SEARCH_QUERY_PARAMS = {
    SORT: 'sort' as const,
    REFINE: 'refine' as const,
    Q: 'q' as const,
    SELECT: 'select' as const,
    CURRENCY: 'currency' as const,
    LOCALE: 'locale' as const,
    EXPAND: 'expand' as const,
    ALL_IMAGES: 'allImages' as const,
    PER_PRICEBOOK: 'perPricebook' as const,
    LIMIT: 'limit' as const,
    OFFSET: 'offset' as const,
    SITE_ID: 'siteId' as const,
    PAGE: 'page' as const,
} as const satisfies Record<string, ProductSearchQueryKeys>;

/**
 * Type-safe query parameter constants for content search
 * These constants ensure we use the correct parameter names as defined in the SCAPI client types
 */
/** Content search folder scope. Always applied by the listing loader, never shown as a user filter. */
export const CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID = 'fdid';

export const CONTENT_SEARCH_QUERY_PARAMS = {
    SORT: 'sort' as const,
    REFINE: 'refine' as const,
    Q: 'q' as const,
    LOCALE: 'locale' as const,
    LIMIT: 'limit' as const,
    OFFSET: 'offset' as const,
    PAGE: 'page' as const,
    SITE_ID: 'siteId' as const,
} as const satisfies Record<string, ContentSearchQueryKeys>;

/**
 * Type-safe helper to get query parameter values
 * @param searchParams - URLSearchParams object
 * @param param - The query parameter key
 * @returns The parameter value or empty string
 */
export function getQueryParam(
    searchParams: URLSearchParams,
    param: ProductSearchQueryKeys | ContentSearchQueryKeys
): string {
    return searchParams.get(param) ?? '';
}

/**
 * Type-safe helper to get all query parameter values for array parameters
 * @param searchParams - URLSearchParams object
 * @param param - The query parameter key
 * @returns Array of parameter values
 */
export function getAllQueryParams(
    searchParams: URLSearchParams,
    param: ProductSearchQueryKeys | ContentSearchQueryKeys
): string[] {
    return searchParams.getAll(param);
}
